// src/components/modules/TAI/MenuContextualTAI.js

import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Menu, Typography, message } from "antd";
import {
  HistoryOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  EditOutlined,
  SwapOutlined,
  CarOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import api from "../../../axiosConfig";
import { useUser } from "../../../UserContext";
import HistorialTrasladoDialog from "../Dialog/HistorialTrasladoDialog";
import ConfirmarTrasladoDialog from "../Dialog/ConfirmarTrasladoDialog";
import ValidarTrasladoDialog from "../Dialog/ValidarTrasladoDialog";
import DesconfirmarTrasladoDialog from "../Dialog/DesconfirmarTrasladoDialog";
import DesvalidarTrasladoDialog from "../Dialog/DesvalidarTrasladoDialog";
import CerrarTrasladoDialog from "../Dialog/CerrarTrasladoDialog";
import EditarTrasladoDialog from "../Dialog/EditarTrasladoDialog";
import CambiarEstadoTrasladoDialog from "../Dialog/CambiarEstadoTrasladoDialog";
import VerTrasladoDialog from "../Dialog/VerTrasladoDialog";
import TAIAsignacionMasiva from "./TAIAsignacionMasiva";

const MenuContextualTAI = ({
  traslado,
  contextMenu,
  onClose,
  onRefresh,
  hasPermission,
}) => {
  const { user } = useUser();

  // Estados de los diálogos
  const [historialDialogOpen, setHistorialDialogOpen] = useState(false);
  const [confirmarDialogOpen, setConfirmarDialogOpen] = useState(false);
  const [desconfirmarDialogOpen, setDesconfirmarDialogOpen] = useState(false);
  const [validarDialogOpen, setValidarDialogOpen] = useState(false);
  const [desvalidarDialogOpen, setDesvalidarDialogOpen] = useState(false);
  const [cerrarDialogOpen, setCerrarDialogOpen] = useState(false);
  const [editarDialogOpen, setEditarDialogOpen] = useState(false);
  const [cambiarEstadoDialogOpen, setCambiarEstadoDialogOpen] = useState(false);
  const [transportadoraDialogOpen, setTransportadoraDialogOpen] = useState(false);
  const [verTrasladoDialogOpen, setVerTrasladoDialogOpen] = useState(false);

  // Guardar referencia al traslado actual para los diálogos
  const [currentTraslado, setCurrentTraslado] = useState(null);

  // Handler para clics en el menú
  const handleMenuClick = useCallback(({ key }) => {
    // Guardar referencia al traslado antes de cerrar el menú
    setCurrentTraslado(traslado);

    switch (key) {
      case "ver-traslado":
        setVerTrasladoDialogOpen(true);
        break;
      case "historial":
        setHistorialDialogOpen(true);
        break;
      case "confirmar":
        setConfirmarDialogOpen(true);
        break;
      case "desconfirmar":
        setDesconfirmarDialogOpen(true);
        break;
      case "validar":
        setValidarDialogOpen(true);
        break;
      case "desvalidar":
        setDesvalidarDialogOpen(true);
        break;
      case "cerrar":
        setCerrarDialogOpen(true);
        break;
      case "editar":
        setEditarDialogOpen(true);
        break;
      case "cambiar-estado":
        setCambiarEstadoDialogOpen(true);
        break;
      case "transportadora":
        setTransportadoraDialogOpen(true);
        break;
      default:
        break;
    }

    onClose();
  }, [traslado, onClose]);

  // Handlers para confirmar/validar
  const handleConfirmar = useCallback(async () => {
    if (!currentTraslado) return;
    try {
      await api.put(`/traslados/confirmar/${currentTraslado.id}`, {
        usuario_id: user.id,
      });
      message.success("Traslado confirmado exitosamente");
      setConfirmarDialogOpen(false);
      setCurrentTraslado(null);
      onRefresh();
    } catch (error) {
      console.error("Error al confirmar traslado:", error);
      message.error("Error al confirmar el traslado");
    }
  }, [currentTraslado, user?.id, onRefresh]);

  const handleDesconfirmar = useCallback(async () => {
    if (!currentTraslado) return;
    try {
      await api.put(`/traslados/desconfirmar/${currentTraslado.id}`, {
        usuario_id: user.id,
      });
      message.success("Traslado desconfirmado exitosamente");
      setDesconfirmarDialogOpen(false);
      setCurrentTraslado(null);
      onRefresh();
    } catch (error) {
      console.error("Error al desconfirmar traslado:", error);
      message.error("Error al desconfirmar el traslado");
    }
  }, [currentTraslado, user?.id, onRefresh]);

  const handleValidar = useCallback(async () => {
    if (!currentTraslado) return;
    try {
      await api.put(`/traslados/validar/${currentTraslado.id}`, {
        usuario_id: user.id,
      });
      message.success("Traslado validado exitosamente");
      setValidarDialogOpen(false);
      setCurrentTraslado(null);
      onRefresh();
    } catch (error) {
      console.error("Error al validar traslado:", error);
      message.error("Error al validar el traslado");
    }
  }, [currentTraslado, user?.id, onRefresh]);

  const handleDesvalidar = useCallback(async () => {
    if (!currentTraslado) return;
    try {
      await api.put(`/traslados/desvalidar/${currentTraslado.id}`, {
        usuario_id: user.id,
      });
      message.success("Traslado desvalidado exitosamente");
      setDesvalidarDialogOpen(false);
      setCurrentTraslado(null);
      onRefresh();
    } catch (error) {
      console.error("Error al desvalidar traslado:", error);
      message.error("Error al desvalidar el traslado");
    }
  }, [currentTraslado, user?.id, onRefresh]);

  // Handler para cerrar traslado
  // El tercer parámetro 'alreadyProcessed' indica si el cierre ya fue procesado por OTP
  const handleCerrarTraslado = useCallback(async (estado, mensaje, alreadyProcessed = false) => {
    if (!currentTraslado) return;

    // Si ya fue procesado por OTP (estado Finalizado), solo actualizar UI
    if (alreadyProcessed) {
      setCerrarDialogOpen(false);
      setCurrentTraslado(null);
      onRefresh();
      return;
    }

    // Cierre normal (Cancelado u otro estado sin OTP)
    try {
      await api.put(`/traslados/cerrar/${currentTraslado.id}`, {
        estado,
        usuario_id: user.id,
        mensaje: mensaje || "No se proporcionó motivo",
      });
      message.success(`Traslado ${estado.toLowerCase()} exitosamente`);
      setCerrarDialogOpen(false);
      setCurrentTraslado(null);
      onRefresh();
    } catch (error) {
      console.error("Error al cerrar traslado:", error);
      message.error("Error al cerrar el traslado");
    }
  }, [currentTraslado, user?.id, onRefresh]);

  // Determinar estados permitidos para cerrar según el status del traslado
  // Para TAI no se permite "Fallido"
  const allowedCloseStates = useMemo(() => {
    if (!currentTraslado) return ["Finalizado", "Cancelado"];
    return currentTraslado.status === "Programado"
      ? ["Cancelado"]
      : ["Finalizado", "Cancelado"];
  }, [currentTraslado]);

  // Handler para cambiar estado del traslado
  const handleCambiarEstado = useCallback(async (nuevoEstado, motivo) => {
    if (!currentTraslado) return;
    try {
      await api.put(`/centro-control/traslados/${currentTraslado.id}/cambiar-estado`, {
        nuevoEstado,
        motivo_cambio: motivo,
        usuario_id: user.id,
      });
      message.success(`Estado cambiado a ${nuevoEstado} exitosamente`);
      setCambiarEstadoDialogOpen(false);
      setCurrentTraslado(null);
      onRefresh();
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      message.error("Error al cambiar el estado del traslado");
    }
  }, [currentTraslado, user?.id, onRefresh]);

  // Función para verificar si se puede editar (dentro de 2 horas antes de la recogida)
  const puedeEditar2Horas = useCallback((unTraslado) => {
    if (!unTraslado?.fecha || !unTraslado?.hora_recogida) {
      return false;
    }

    try {
      const fechaSolo = unTraslado.fecha.split('T')[0];
      const fechaHoraString = `${fechaSolo}T${unTraslado.hora_recogida}`;
      const fechaHoraRecogida = new Date(fechaHoraString);
      const ahora = new Date();
      const dosHorasAntes = new Date(fechaHoraRecogida.getTime() - 2 * 60 * 60 * 1000);

      return ahora < dosHorasAntes;
    } catch (error) {
      console.error("Error al validar tiempo de edición:", error);
      return false;
    }
  }, []);

  // Construir items del menú
  const menuItems = useMemo(() => {
    if (!traslado) return [];

    const items = [];

    // Ver Traslado - siempre visible
    if (hasPermission("VISUALIZAR_TAI")) {
      items.push({
        key: "ver-traslado",
        icon: <EyeOutlined style={{ color: "#722ed1", fontSize: "16px" }} />,
        label: (
          <Typography.Text style={{ color: "#0a2e5c", fontWeight: 500, fontSize: "14px" }}>
            Ver Traslado
          </Typography.Text>
        ),
        style: { padding: "10px 12px", borderRadius: "4px", margin: "1px 0", minHeight: "40px" },
      });
    }

    // Historial - siempre visible
    if (hasPermission("VISUALIZAR_TAI")) {
      items.push({
        key: "historial",
        icon: <HistoryOutlined style={{ color: "#722ed1", fontSize: "16px" }} />,
        label: (
          <Typography.Text style={{ color: "#0a2e5c", fontWeight: 500, fontSize: "14px" }}>
            Historial
          </Typography.Text>
        ),
        style: { padding: "10px 12px", borderRadius: "4px", margin: "1px 0", minHeight: "40px" },
      });
    }

    // Editar Traslado (usando VISUALIZAR_TAI temporalmente mientras se investiga EDITAR_TAI)
    const estaCerrado = ["Finalizado", "Cancelado", "Fallido"].includes(traslado.status);
    if (hasPermission("VISUALIZAR_TAI")) {
      const puedeEditar = !estaCerrado && puedeEditar2Horas(traslado);
      items.push({
        key: "editar",
        icon: <EditOutlined style={{ color: puedeEditar ? "#722ed1" : "#8c8c8c", fontSize: "16px" }} />,
        label: (
          <Typography.Text style={{ color: puedeEditar ? "#0a2e5c" : "#8c8c8c", fontWeight: 500, fontSize: "14px" }}>
            Editar Traslado
          </Typography.Text>
        ),
        style: { padding: "10px 12px", borderRadius: "4px", margin: "1px 0", minHeight: "40px" },
        disabled: !puedeEditar,
      });
    }

    // Cambiar Transportadora (solo para traslados no cerrados)
    if (hasPermission("VISUALIZAR_TAI") && !estaCerrado) {
      items.push({
        key: "transportadora",
        icon: <CarOutlined style={{ color: "#722ed1", fontSize: "16px" }} />,
        label: (
          <Typography.Text style={{ color: "#0a2e5c", fontWeight: 500, fontSize: "14px" }}>
            Cambiar Transportadora
          </Typography.Text>
        ),
        style: { padding: "10px 12px", borderRadius: "4px", margin: "1px 0", minHeight: "40px" },
      });
    }

    // Separador
    if (items.length > 0) {
      items.push({ type: "divider" });
    }

    // Confirmar/Desconfirmar
    if (hasPermission("CONFIRMACION_TAI")) {
      const estaConfirmado = traslado.confirmacion === "Confirmado";

      if (estaConfirmado) {
        items.push({
          key: "desconfirmar",
          icon: <CloseCircleOutlined style={{ color: "#f5222d", fontSize: "16px" }} />,
          label: (
            <Typography.Text style={{ color: "#f5222d", fontWeight: 500, fontSize: "14px" }}>
              Desconfirmar
            </Typography.Text>
          ),
          style: { padding: "10px 12px", borderRadius: "4px", margin: "1px 0", minHeight: "40px" },
        });
      } else {
        items.push({
          key: "confirmar",
          icon: <CheckCircleOutlined style={{ color: "#722ed1", fontSize: "16px" }} />,
          label: (
            <Typography.Text style={{ color: "#0a2e5c", fontWeight: 500, fontSize: "14px" }}>
              Confirmar
            </Typography.Text>
          ),
          style: { padding: "10px 12px", borderRadius: "4px", margin: "1px 0", minHeight: "40px" },
        });
      }
    }

    // Validar/Desvalidar
    if (hasPermission("VALIDACION_TAI")) {
      const estaValidado = traslado.seguimiento === "Validado";

      if (estaValidado) {
        items.push({
          key: "desvalidar",
          icon: <CloseCircleOutlined style={{ color: "#f5222d", fontSize: "16px" }} />,
          label: (
            <Typography.Text style={{ color: "#f5222d", fontWeight: 500, fontSize: "14px" }}>
              Desvalidar
            </Typography.Text>
          ),
          style: { padding: "10px 12px", borderRadius: "4px", margin: "1px 0", minHeight: "40px" },
        });
      } else {
        items.push({
          key: "validar",
          icon: <SafetyCertificateOutlined style={{ color: "#722ed1", fontSize: "16px" }} />,
          label: (
            <Typography.Text style={{ color: "#0a2e5c", fontWeight: 500, fontSize: "14px" }}>
              Validar
            </Typography.Text>
          ),
          style: { padding: "10px 12px", borderRadius: "4px", margin: "1px 0", minHeight: "40px" },
        });
      }
    }

    // Cambiar Estado (solo si está cerrado)
    if (hasPermission("CAMBIAR_ESTADO_TAI") && estaCerrado) {
      // Verificar si el traslado está cancelado
      const isCancelado = traslado.status?.toUpperCase() === 'CANCELADO';
      // Verificar si tiene móvil asignado
      const tieneMovil = traslado.vehiculo_id || traslado.conductor_id;
      // Deshabilitado si está cancelado y no tiene móvil
      const shouldDisable = isCancelado && !tieneMovil;

      items.push({
        key: "cambiar-estado",
        icon: <SwapOutlined style={{ color: shouldDisable ? "#8c8c8c" : "#722ed1", fontSize: "16px" }} />,
        label: (
          <Typography.Text style={{ color: shouldDisable ? "#8c8c8c" : "#0a2e5c", fontWeight: 500, fontSize: "14px" }}>
            {shouldDisable ? "Cambiar Estado (Sin Móvil)" : "Cambiar Estado"}
          </Typography.Text>
        ),
        style: { padding: "10px 12px", borderRadius: "4px", margin: "1px 0", minHeight: "40px" },
        disabled: shouldDisable,
      });
    }

    // Separador antes de cerrar traslado
    if (hasPermission("CERRAR_TAI") && items.length > 0) {
      items.push({ type: "divider" });
    }

    // Cerrar Traslado
    if (hasPermission("CERRAR_TAI")) {
      items.push({
        key: "cerrar",
        icon: <CloseOutlined style={{ color: estaCerrado ? "#8c8c8c" : "#f5222d", fontSize: "16px" }} />,
        label: (
          <Typography.Text style={{ color: estaCerrado ? "#8c8c8c" : "#f5222d", fontWeight: 500, fontSize: "14px" }}>
            {estaCerrado ? "Traslado cerrado" : "Cerrar traslado"}
          </Typography.Text>
        ),
        style: { padding: "10px 12px", borderRadius: "4px", margin: "1px 0", minHeight: "40px" },
        disabled: estaCerrado,
      });
    }

    return items;
  }, [traslado, hasPermission, puedeEditar2Horas]);

  // Posicionamiento inteligente del menú
  const getSmartMenuPosition = useCallback((mouseX, mouseY) => {
    const menuWidth = 220;
    const itemHeight = 42;
    const menuPadding = 16;
    const menuHeight = menuItems.length * itemHeight + menuPadding;

    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const margin = 15;

    let adjustedX = mouseX + 5;
    if (adjustedX + menuWidth > windowWidth - margin) {
      adjustedX = mouseX - menuWidth - 5;
    }
    if (adjustedX < margin) {
      adjustedX = margin;
    }

    let adjustedY = mouseY + 5;
    if (adjustedY + menuHeight > windowHeight - margin) {
      adjustedY = mouseY - menuHeight - 5;
    }
    if (adjustedY < margin) {
      adjustedY = margin;
    }

    return { x: Math.round(adjustedX), y: Math.round(adjustedY) };
  }, [menuItems.length]);

  const position = contextMenu ? getSmartMenuPosition(contextMenu.mouseX, contextMenu.mouseY) : { x: 0, y: 0 };

  // Obtener el ID del traslado para los diálogos
  const dialogTrasladoId = currentTraslado?.id || traslado?.id;

  return (
    <>
      {/* Menú contextual - solo se muestra si hay contextMenu y traslado */}
      {contextMenu && traslado && (
        <>
          {/* Overlay para cerrar el menú al hacer clic fuera */}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              backgroundColor: "transparent",
            }}
            onClick={onClose}
            onContextMenu={(e) => {
              e.preventDefault();
              onClose();
            }}
          />

          {/* Menú contextual */}
          <div
            style={{
              position: "fixed",
              top: position.y,
              left: position.x,
              zIndex: 10000,
            }}
          >
            <Menu
              style={{
                borderRadius: "8px",
                boxShadow: "0 6px 24px rgba(114, 46, 209, 0.15), 0 2px 8px rgba(0, 0, 0, 0.08)",
                border: "1px solid #f0e6ff",
                backgroundColor: "#ffffff",
                minWidth: "200px",
                maxWidth: "260px",
                padding: "8px",
              }}
              className="tai-context-menu"
              mode="vertical"
              items={menuItems}
              onClick={handleMenuClick}
            />
          </div>

          {/* Estilos personalizados */}
          <style>
            {`
              .tai-context-menu .ant-menu-item:hover {
                background-color: #f9f0ff !important;
              }
              .tai-context-menu .ant-menu-item-divider {
                margin: 4px 0;
                background-color: #f0e6ff;
              }
            `}
          </style>
        </>
      )}

      {/* Diálogos - siempre se renderizan para mantener su estado */}
      <HistorialTrasladoDialog
        open={historialDialogOpen}
        onClose={() => {
          setHistorialDialogOpen(false);
          setCurrentTraslado(null);
        }}
        trasladoId={dialogTrasladoId}
      />

      <ConfirmarTrasladoDialog
        open={confirmarDialogOpen}
        onClose={() => {
          setConfirmarDialogOpen(false);
          setCurrentTraslado(null);
        }}
        onConfirm={handleConfirmar}
        trasladoId={`CS-${dialogTrasladoId}`}
      />

      <DesconfirmarTrasladoDialog
        open={desconfirmarDialogOpen}
        onClose={() => {
          setDesconfirmarDialogOpen(false);
          setCurrentTraslado(null);
        }}
        onDesconfirmar={handleDesconfirmar}
        trasladoId={`CS-${dialogTrasladoId}`}
      />

      <ValidarTrasladoDialog
        open={validarDialogOpen}
        onClose={() => {
          setValidarDialogOpen(false);
          setCurrentTraslado(null);
        }}
        onSubmit={handleValidar}
        trasladoId={`CS-${dialogTrasladoId}`}
      />

      <DesvalidarTrasladoDialog
        open={desvalidarDialogOpen}
        onClose={() => {
          setDesvalidarDialogOpen(false);
          setCurrentTraslado(null);
        }}
        onDesvalidar={handleDesvalidar}
        trasladoId={`CS-${dialogTrasladoId}`}
      />

      <CerrarTrasladoDialog
        open={cerrarDialogOpen}
        onClose={() => {
          setCerrarDialogOpen(false);
          setCurrentTraslado(null);
        }}
        onSubmit={handleCerrarTraslado}
        allowedStates={allowedCloseStates}
        isTAI={true}
        trasladoId={currentTraslado?.id || traslado?.id}
        traslado={currentTraslado || traslado}
      />

      <EditarTrasladoDialog
        open={editarDialogOpen}
        onClose={() => {
          setEditarDialogOpen(false);
          setCurrentTraslado(null);
        }}
        traslado={currentTraslado || traslado}
        onUpdated={onRefresh}
      />

      <CambiarEstadoTrasladoDialog
        open={cambiarEstadoDialogOpen}
        onClose={() => {
          setCambiarEstadoDialogOpen(false);
          setCurrentTraslado(null);
        }}
        onConfirm={handleCambiarEstado}
        estadoActual={(currentTraslado || traslado)?.status}
        excludeEstados={["Fallido"]}
      />

      <TAIAsignacionMasiva
        open={transportadoraDialogOpen}
        onClose={() => {
          setTransportadoraDialogOpen(false);
          setCurrentTraslado(null);
        }}
        trasladoIds={dialogTrasladoId ? [dialogTrasladoId] : []}
        onSuccess={() => {
          setTransportadoraDialogOpen(false);
          setCurrentTraslado(null);
          onRefresh();
        }}
      />

      <VerTrasladoDialog
        open={verTrasladoDialogOpen}
        onClose={() => {
          setVerTrasladoDialogOpen(false);
          setCurrentTraslado(null);
        }}
        traslado={currentTraslado || traslado}
      />
    </>
  );
};

MenuContextualTAI.propTypes = {
  traslado: PropTypes.object,
  contextMenu: PropTypes.shape({
    mouseX: PropTypes.number.isRequired,
    mouseY: PropTypes.number.isRequired,
  }),
  onClose: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  hasPermission: PropTypes.func.isRequired,
};

export default MenuContextualTAI;
